/**
 * Placeholder Widget
 *
 * Reusable empty-chat tile for lightweight widget shortcuts.
 *
 * Primary responsibilities:
 * - Render a compact tile label and optional icon
 * - Avoid invalid nested interactive HTML when widget content has buttons
 *
 * Dependencies:
 * - Empty widget surface styles
 */

'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { EMPTY_WIDGET_SURFACE_CLASS } from './widget-surface'

interface PlaceholderWidgetProps {
  title: string
  icon?: React.ReactNode
  className?: string
  onClick?: () => void
  children?: React.ReactNode
}

export function PlaceholderWidget({
  title,
  icon,
  className,
  onClick,
  children
}: PlaceholderWidgetProps) {
  const hasChildren = React.Children.count(children) > 0
  const content = (
    <>
      {icon && <div className="mb-1.5 text-muted-foreground">{icon}</div>}
      <span className="text-sm font-medium text-foreground">{title}</span>
      {children}
    </>
  )

  if (!hasChildren) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(EMPTY_WIDGET_SURFACE_CLASS, 'flex-col items-start justify-center', className)}
        aria-label={title}
      >
        {content}
      </button>
    )
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick || event.target !== event.currentTarget) return
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    onClick()
  }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cn(EMPTY_WIDGET_SURFACE_CLASS, 'flex-col items-start justify-center', className)}
      aria-label={title}
    >
      {content}
    </div>
  )
}
